import { FieldState, FormStateLike, ValidationsForForm, ValidationsResponse, Validator, ValidatorResult } from "@/interfaces/api/forms/forms_types"
import { Devices, DeviceType } from "@/interfaces/genericTypes.types"
import { Base64 } from "js-base64"
import { HIDDEN_MENU_PAGES } from "./constants"
import dayjs from "dayjs"
import { useLogout } from "@/apiUtilities/mutations/useLogout"
import { useCompleteAccountStore } from "@/stores/useCompleteAccountStore"
import { useUserStore } from "@/stores/useUserStore"

/**
 * Encodes a string in base64
 * @param {*} param a random string
 * @returns string encoded in base64
 */
export function toBase64(param: string): string {
  return btoa(
    encodeURIComponent(param).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
      return String.fromCharCode(parseInt(p1, 16));
    })
  );
}
/**
 * Encodes a string in base64 URI friendly
 * @param {*} param a random string
 * @returns string encoded in base64
 */
export function toBase64URI (param:string): string {
  return Base64.encodeURI(param)
}

/**
 * Validates every field in the form and returns:
 * - `validations`: pre-field validator results (used to render constraint tooltips)
 * - `isValid`: overall form validity (true only if all validators succeed)
 * @param formState Current form state to validate.
 * @returns Object containing pre-field validations and overall validity flag.
 */
export function validateForm<T>(formState: T, initialValidations: ValidationsForForm<T>): ValidationsResponse<T> {
  let isValid = true
  const validations: ValidationsForForm<T> = { ...initialValidations }

  for (const key in formState) {
    const field = formState[key as keyof T] as FieldState<any>

    const fieldValidations = field.validators.map((elem: Validator) => {
      const result = elem.validator(field.value, formState)
      return {
        success: result.success,
        msg: result.msg || (result.success ? `${field} OK` : `${field} Error`),
        show: elem.show,
        params: result.params
      }
    })

    if (fieldValidations.some((v) => !v.success)) isValid = false

    validations[key as keyof T] = fieldValidations
  }

  return { validations, isValid }
}

/**
 * Returns the validation state of a specific form field.
 * 
 * It checks whether the field has been marked as "dirty" (user has interacted with it)
 * and if any validation rule has failed (excluding the "required" rule).
 * 
 * @param formState - The form state we want to validate.
 * @param validations - Validation result for this field.
 * @param fieldName - The name of the field in the LoginFormState to evaluate.
 * @param enabledRequired - Enable it to return a “required field” error.
 * @returns An object containing:
 * - hasError: boolean indicating if the field currently has a validation error.
 * - msg: the validation message key of the first failing rule (excluding "required"), or undefined if there is no error.
 */
export function getFieldError<T extends FormStateLike<T>>(formState: T, validations: ValidationsForForm<T>, fieldName: keyof T, enabledRequired: boolean = false): { hasError: boolean; msg: string | undefined } {
  const field = formState[fieldName]
  const validation = validations[fieldName]
  const condition = enabledRequired
    ? (v: ValidatorResult) => !v.success
    : (v: ValidatorResult) => !v.success && v.msg !== 'required'
  
  const hasValidationError = field.dirty && validation.some(condition)
  
  return {
    hasError: hasValidationError,
    msg: hasValidationError ? validation.find(condition)!.msg : undefined
  }
}

export function removeLocaleFromPath(path: string): string {
  return path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/|$)/i, '') || '/'
}

/**
 * Helper function to determine if the main menu should be hidden based on the current pathname.
 * 
 * @param pathname - The current URL pathname
 * @returns boolean indicating if the menu should be hidden
 */
export function shouldHideMenu(pathname: string, queryString?: string): boolean {
  if (queryString && queryString.includes('registration=true')) {
    return true
  }
  return HIDDEN_MENU_PAGES.some(page => pathname.startsWith(page))
}

export function parseDevice(userAgent: string): DeviceType {
  const ua = userAgent || ''
  if (/android/i.test(ua)) return Devices.ANDROID
  if (/iPad|iPhone|iPod/i.test(ua)) return Devices.IOS
  if (/Win/i.test(ua)) return Devices.WINDOWS
  if (/Mac/i.test(ua)) return Devices.MAC
  return Devices.UNKNOWN
}

/**
 *  Normalizes a date string to "YYYY-MM-DD" format
 * 
 * @param value  - A date string (ISO or any format supported by dayjs)
 * @returns A string formatted as "YYYY-MM-DD", or empty string if invalid
 */
export function toYmd(value?: string) {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('YYYY-MM-DD') : '' 
}

export function formatTime(dateString: string, locale: string) {
  return new Date(dateString).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

/**
 * Formats a duration (in milliseconds) into a human-readable string.
 *
 * Rules:
 * - If duration >= 1 hour → format as "HH mm"
 * - If duration >= 1 minute → show minutes only
 * - If duration < 1 minute → show seconds
 *
 * The output is localized using the provided translation function.
 *
 * @param ms - Duration in milliseconds
 * @param t - Translation function from next-intl
 * @returns Localized formatted duration string
 */
export function formatDuration(
  ms: number,
  t: (key: string, values?: Record<string, any>) => string
): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))

  const hours = Math.floor(totalSeconds / 3600)
  const remainingSeconds = totalSeconds % 3600

  const hasAtLeastOneMinute = totalSeconds >= 60

  // minutos: solo redondeamos si hay al menos 1 minuto
  let minutes = hasAtLeastOneMinute
    ? Math.round(remainingSeconds / 60)
    : 0

  // ajuste si llega a 60 min
  let adjustedHours = hours
  if (minutes === 60) {
    adjustedHours += 1
    minutes = 0
  }

  const seconds = totalSeconds % 60

  // Case 1: Duration includes hours → HH mm
  if (adjustedHours > 0) {
    return t('duration.hoursMinutes', {
      hours: adjustedHours,
      minutes: minutes.toString().padStart(2, '0')
    })
  }

  // Case 2: Duration includes minutes → mm
  if (minutes > 0) {
    return t('duration.minutes', { minutes })
  }

  // Case 3: Less than 1 minute → ss
  return t('duration.seconds', { seconds })
}

export const pushToDataLayer = (data: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(data)
  }
}

export async function fetcher(input: RequestInfo, init?: RequestInit) {

  let absoluteUrl = input;
  if (typeof input === 'string' && input.startsWith('/')) {
    // Siempre intenta usar window.location.origin
    /*let origin = '';
    if (typeof window !== 'undefined' && window.location) {
      origin = window.location.origin;
    } else {
      // Fallback para SSR: intenta obtener el host de headers si se pasa en init.headers
      if (init && init.headers && typeof init.headers === 'object') {
        const headers = init.headers as Record<string, string>;
        origin = headers['x-forwarded-host']
          ? `https://${headers['x-forwarded-host']}`
          : headers['host']
            ? `https://${headers['host']}`
            : '';
      }
    } */
    const origin = process.env.NEXT_PUBLIC_SITE_URL
    absoluteUrl = origin ? origin + input : input;
  }

  const res = await fetch(absoluteUrl, {
    ...init,
    credentials: "include", // importante si usas cookies
  });

  // Solo ejecutar el if si existe la cookie userId
  function getCookie(name: string) {
    if (typeof document === 'undefined') return null;
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  }

  // TODO: change if
  const userIdCookie = getCookie('userId');

  if (res.status === 401) {
    let eventType = 'complete_account_required'

    try {
      const jsonData = await res.clone().json()
      if(!jsonData.error || !jsonData.error.data) {
         if(userIdCookie) {
          let eventType = 'unauthorized'
          window.dispatchEvent(new Event(eventType));
          throw new Error(eventType)
        }
        return
      }
      const data = jsonData.error.data
      if (data?.missing_mandatory_fields || data?.missing_actions) {
        eventType = 'complete_account_required'
        
        // Detectar si venimos de /login/[reseller]
        const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
        const isResellerFlow = pathname.includes('/login/')
        
        useCompleteAccountStore.setState({
          data: {
            missing_mandatory_fields: data?.missing_mandatory_fields,
            missing_actions: data?.missing_actions,
          },
        })
        
        // Establecer reseller en userStore si venimos de /login/[reseller]
        if (isResellerFlow) {
          useUserStore.setState({ reseller: true })
        }
        
        window.dispatchEvent(new Event(eventType));
      } else if(userIdCookie){
          let eventType = 'unauthorized'
          window.dispatchEvent(new Event(eventType));
          throw new Error(eventType)
      }
    } catch (error) {
      // ignore parse errors and fall back to unauthorized
    }

    // throw new Error(eventType)
  }

  return res;
}